/**
 * Props contract of the schematic Argentina map.
 *
 * The component is purely presentational: which jurisdictions exist, what
 * status each one has and every string the user reads arrive as props. Data
 * fetching, status derivation and i18n belong to the consumer — the site
 * feeds it its static launch table, the teacher UI feeds it live coverage.
 */

/**
 * Coverage statuses a cell can render. Consumers use the subset they need.
 *
 * `partial` exists because a province is not one document. With the old rule a
 * single structured document painted the whole jurisdiction as covered — Jujuy
 * went blue carrying thirteen citable documents behind it — and blue promises
 * navigation by axis and block that those thirteen cannot give.
 *
 * What separates the statuses is measured on the statements and not on the
 * documents: the share of loaded statements that carry an area AND a year, so
 * a promotion that relabels a document's tier without writing those two
 * coordinates moves nothing (ADR-0039). Consumers derive the status; this
 * package only draws what it is handed.
 */
export type MapStatus = 'covered' | 'partial' | 'progress' | 'citable' | 'pending';

/** Canonical legend order; the legend shows the subset the labels declare. */
export const MAP_STATUS_ORDER: readonly MapStatus[] = [
  'covered',
  'partial',
  'progress',
  'citable',
  'pending',
];

export interface MapGridPosition {
  /** 0 = north, 9 = south. */
  readonly row: number;
  /** 0 = west, 4 = east. */
  readonly col: number;
}

/** One term/value pair shown in the detail panel under the status line. */
export interface MapDetailField {
  readonly term: string;
  readonly value: string;
}

export interface MapJurisdiction {
  readonly id: string;
  /** 3-4 letter canonical code shown inside the cell (e.g. JUJ, BSAS). */
  readonly code: string;
  readonly name: string;
  readonly status: MapStatus;
  readonly gridPosition: MapGridPosition;
  /** Extra lines for the detail panel (design title, counts, ...). */
  readonly detail?: readonly MapDetailField[];
}

export interface MapStatusLabel {
  readonly label: string;
  readonly description: string;
}

/** Every string the map renders, injected so each consumer owns its copy. */
export interface ArgentinaMapLabels {
  /** aria-label of the grid group. */
  readonly mapLabel: string;
  /** Kicker above the jurisdiction name in the detail panel. */
  readonly detailKicker: string;
  /** Panel copy when no cell is hovered or focused. */
  readonly detailIdle: string;
  /** Heading of the legend. */
  readonly legendTitle: string;
  /** Labels of the statuses in play; the legend lists exactly these. */
  readonly statuses: Partial<Record<MapStatus, MapStatusLabel>>;
}
